/**
 * BoardRichEditor — 게시판 전용 TipTap 에디터 래퍼
 *
 * 기존 TipTapEditor/EditorMenuBar 컴포넌트를 재활용하되,
 * 게시판 모듈에 필요한 인터페이스(JSON ⇄ plain_text 분리 등)를 제공.
 *
 * Props:
 *  - initialContent: TipTap JSON 또는 HTML 문자열 (편집 시 초기값)
 *  - onChange:        에디터 변경 시 (json, plainText) 콜백
 *  - editable:        편집 가능 여부 (기본 true)
 *  - placeholder:     빈 에디터 안내 텍스트
 *  - minHeight:       에디터 최소 높이 (기본 200px)
 */
import React, { useEffect, useCallback, useRef } from 'react';
import { t } from '@lingui/macro';
import { useEditor, EditorContent } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import { common, createLowlight } from 'lowlight';
import { EditorMenuBar } from '../components/editor/EditorMenuBar';
import { MentionUser } from './MentionSuggestion';
import {
  createUnifiedMentionSuggestion,
  MentionUserItem,
  MentionPageItem,
} from './UnifiedMentionSuggestion';
import '../components/TipTapEditor.scss';

// lowlight에 common 언어팩 등록 (js, ts, python, css, html, json, bash 등)
const lowlight = createLowlight(common);

interface BoardRichEditorProps {
  initialContent?: any;            // TipTap JSON 또는 HTML string
  onChange?: (json: any, plainText: string) => void;
  editable?: boolean;
  placeholder?: string;
  minHeight?: number;
  /** 이미지 파일 → URL 변환 콜백 (Storage 업로드). 미제공 시 이미지 삽입 비활성화 */
  onImageUpload?: (file: File) => Promise<string>;
  /** @멘션 대상 멤버 목록. 제공 시 멘션 기능 활성화 */
  mentionMembers?: MentionUser[];
  /** 위키 게시판의 페이지 목록. 제공 시 같은 `@` 트리거로 페이지 멘션(위키링크) 가능 */
  wikiPages?: MentionPageItem[];
  /** 읽기 모드에서 위키 페이지 멘션 클릭 시 호출 (위키링크 네비게이션) */
  onWikiPageClick?: (pageId: string) => void;
  /** @전체 멘션 권한 — 권한 있으면 `@` 드롭다운 최상단에 "전체" 항목 노출 */
  canMentionAll?: boolean;
  /** 좁은 컨테이너 모바일 스타일 — 읽기 모드 본문 폰트 축소 (.tiptap-editor--narrow) */
  narrow?: boolean;
}

const BoardRichEditor: React.FC<BoardRichEditorProps> = ({
  initialContent,
  onChange,
  editable = true,
  placeholder = t`Enter content...`,
  minHeight = 200,
  onImageUpload,
  mentionMembers,
  wikiPages,
  onWikiPageClick,
  canMentionAll,
  narrow = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadRef = useRef(onImageUpload);
  uploadRef.current = onImageUpload;

  // 멘션 멤버 ref (suggestion 내부에서 최신값 참조)
  const membersRef = useRef<MentionUser[]>(mentionMembers ?? []);
  membersRef.current = mentionMembers ?? [];

  // @전체 멘션 권한 ref
  const canMentionAllRef = useRef<boolean>(!!canMentionAll);
  canMentionAllRef.current = !!canMentionAll;

  // 위키 페이지 ref (suggestion 내부에서 최신값 참조)
  const pagesRef = useRef<MentionPageItem[]>(wikiPages ?? []);
  pagesRef.current = wikiPages ?? [];

  /** 이미지 파일을 에디터에 삽입하는 공통 핸들러 */
  const insertImage = useCallback(async (file: File, editorInstance: any) => {
    if (!uploadRef.current || !editorInstance) return;
    try {
      const url = await uploadRef.current(file);
      editorInstance.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error('[BoardRichEditor] Image upload failed:', err);
    }
  }, []);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false, // lowlight 버전으로 교체
      }),
      CodeBlockLowlight.configure({
        lowlight,
        defaultLanguage: 'plaintext',
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder }),
      // @멘션 (mentionMembers 또는 wikiPages 중 하나라도 제공되면 활성화)
      // 통합 dropdown — `@`로 사용자와 위키 페이지 모두 검색·선택 가능
      // attrs.mentionType: 'user' | 'wiki-page' 로 구분
      ...((mentionMembers || wikiPages) ? [
        Mention.extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              mentionType: {
                default: 'user',
                parseHTML: el => el.getAttribute('data-mention-type') ?? 'user',
                renderHTML: attrs => ({ 'data-mention-type': attrs.mentionType }),
              },
            };
          },
        }).configure({
          HTMLAttributes: { class: 'mention' },
          suggestion: createUnifiedMentionSuggestion(
            () => membersRef.current.map(m => ({
              kind: 'user' as const,
              id: m.id,
              name: m.name,
              profileUrl: m.profileUrl,
            })),
            () => pagesRef.current,
            () => canMentionAllRef.current,
          ),
        }),
      ] : []),
    ],
    content: initialContent ?? '',
    editable,
    onUpdate: ({ editor: e }) => {
      onChange?.(e.getJSON(), e.getText());
    },
    editorProps: {
      // 읽기 모드에서 위키 페이지 멘션 클릭 시 onWikiPageClick 콜백 호출
      handleClick: (_view, _pos, event) => {
        if (editable) return false;
        const target = (event.target as HTMLElement | null)?.closest('[data-mention-type="wiki-page"]') as HTMLElement | null;
        if (!target) return false;
        const pageId = target.getAttribute('data-id');
        if (pageId && onWikiPageClick) {
          event.preventDefault();
          onWikiPageClick(pageId);
          return true;
        }
        return false;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved || !uploadRef.current) return false;
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!imageFiles.length) return false;
        event.preventDefault();
        // view.state 에서 editor 인스턴스를 직접 참조할 수 없으므로 별도 처리
        return true; // 기본 동작 차단, onDrop 이벤트에서 처리
      },
      handlePaste: (_view, event) => {
        if (!uploadRef.current) return false;
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) insertImage(file, editor);
            return true;
          }
        }
        return false;
      },
    },
  });

  // editable prop 변경 시 동기화
  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editable, editor]);

  // initialContent 변경 시 콘텐츠 교체 (읽기→편집 전환 시)
  useEffect(() => {
    if (editor && initialContent !== undefined) {
      // 문자열이면 HTML, 객체면 JSON
      if (typeof initialContent === 'string') {
        editor.commands.setContent(initialContent);
      } else if (initialContent && typeof initialContent === 'object') {
        editor.commands.setContent(initialContent);
      }
    }
  }, [initialContent, editor]);

  if (!editor) return null;

  if (!editable) {
    // 읽기 전용 → 메뉴바 없이 콘텐츠만 렌더 (min-height 없이 콘텐츠 크기에 맞춤)
    return (
      <div className={`tiptap-editor tiptap-editor--readonly${narrow ? ' tiptap-editor--narrow' : ''}`}>
        <EditorContent editor={editor} />
      </div>
    );
  }

  /** 파일 선택 다이얼로그로 이미지 업로드 */
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        await insertImage(file, editor);
      }
    }
    e.target.value = ''; // 같은 파일 재선택 허용
  }, [editor, insertImage]);

  /** 드래그 앤 드롭 이벤트 핸들러 (에디터 영역) */
  const handleDrop = useCallback(async (e: React.DragEvent) => {
    if (!onImageUpload || !editor) return;
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    e.preventDefault();
    e.stopPropagation();
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        await insertImage(file, editor);
      }
    }
  }, [editor, onImageUpload, insertImage]);

  return (
    <div className="tiptap-editor flex flex-col border border-border rounded-xl overflow-hidden shadow-sm bg-background/40 transition-all focus-within:shadow-md focus-within:bg-background">
      {/* 툴바 — 솔리드 배경으로 외곽 보더가 라운드 코너에서 blur/alpha 합성으로 인해
          가려지는 시각 효과를 제거. 하단 구분선은 별도 sibling 으로 분리해 round + overflow-hidden 과 매끄럽게 이어짐 */}
      <div className="sticky top-0 z-10 bg-muted px-2 py-1.5">
        <EditorMenuBar editor={editor} onImageClick={onImageUpload ? () => fileInputRef.current?.click() : undefined} />
      </div>
      <div className="h-px bg-border shrink-0" />
      {/* 에디터 본문 */}
      <div
        className="flex-1 p-4 cursor-text overflow-y-auto"
        style={{ minHeight }}
        onClick={() => editor?.commands.focus()}
        onDrop={handleDrop}
        onDragOver={onImageUpload ? (e) => e.preventDefault() : undefined}
      >
        <EditorContent editor={editor} />
      </div>
      {/* 숨겨진 파일 입력 */}
      {onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
      )}
    </div>
  );
};

export default BoardRichEditor;

/** 에디터 인스턴스에서 JSON + plainText를 추출하는 헬퍼 */
export function getEditorOutput(editor: ReturnType<typeof useEditor>) {
  if (!editor) return { json: null, plainText: '' };
  return {
    json: editor.getJSON(),
    plainText: editor.getText(),
  };
}
